import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Button, Popconfirm } from "antd";
import ModuleCard from "../../components/ModuleCard";
import { OrchestratorAPI } from "../../common/api/axios";
import { useAppData } from "../../app/appContext";
import { isOrderFulfillmentModuleEnabled, TENANT_HEADERS } from "../../common/config";
import { serverErrorMessage } from "../../common/utils/errors";
import { toastError, toastSuccess } from "../../common/utils/toast";
import { colors } from "../../app/styles";
import { ReactComponent as OrderIcon } from "../../app/icons/order.svg";
import { ReactComponent as MaterialIcon } from "../../app/icons/material.svg";
import { ReactComponent as CycleCountIcon } from "../../app/icons/cycle-count.svg";

const Page = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 14px;
`;

const SectionLabel = styled.div`
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.textMuted};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// The Exit button is a sibling of the module card (a native <button>), overlaid on
// its right edge — nesting a button inside the card button would be invalid DOM.
const InwardCue = styled.div`
  position: relative;
`;

const ExitSlot = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  z-index: 1;
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 16px;
`;

export default function Home() {
  const navigate = useNavigate();
  const { counts, inwardOrchestrationActive, authEnabled, signOut } =
    useAppData();
  const showOrderFulfillment = isOrderFulfillmentModuleEnabled();
  // Inward operation is open but there is no inward work left → prompt to exit it.
  const showExitInwardCue =
    inwardOrchestrationActive && counts.inward === 0;

  const [exiting, setExiting] = useState(false);

  // Exit the inward operation straight from Home (same endpoint the Inward page
  // uses) so the operator doesn't have to open the module just to end it. On
  // success the App's orchestration poll clears the cue within a refresh cycle.
  const handleExitInward = async () => {
    if (exiting) {
      return;
    }
    setExiting(true);
    try {
      await OrchestratorAPI.post("orchestrations/exitInward", {}, {
        headers: TENANT_HEADERS,
      });
      toastSuccess("Exit requested. Inward will stop after current operations.");
    } catch (error) {
      toastError(serverErrorMessage(error, "Failed to exit inward."));
      setExiting(false);
    }
  };

  const inwardCard = (
    <ModuleCard
      icon={<MaterialIcon />}
      title="Material Inwards"
      description="Verify slot, SKU, and inward tote"
      count={counts.inward}
      notice={showExitInwardCue ? "No tasks left · Exit inward" : undefined}
      onClick={() => navigate("/inward")}
    />
  );

  return (
    <Page>
      <SectionLabel>Select warehouse module</SectionLabel>
      <Cards>
        {showOrderFulfillment && (
          <ModuleCard
            icon={<OrderIcon />}
            title="Order Fulfillment"
            description="Link order totes to put wall grid"
            count={counts.orderToteMapping}
            onClick={() => navigate("/order-tote-mapping")}
          />
        )}
        {showExitInwardCue ? (
          <InwardCue>
            {inwardCard}
            <ExitSlot>
              <Popconfirm
                title="Exit inward operation?"
                description="Ends the whole inward operation. It stops after current work completes."
                okText="Exit"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={handleExitInward}
              >
                <Button danger loading={exiting}>
                  Exit Inward
                </Button>
              </Popconfirm>
            </ExitSlot>
          </InwardCue>
        ) : (
          inwardCard
        )}
        <ModuleCard
          icon={<CycleCountIcon />}
          title="Cycle Count"
          description="Audit physical counts at slot location"
          count={counts.cycleCount}
          onClick={() => navigate("/cycle-count")}
        />
      </Cards>
      {authEnabled && (
        <Footer>
          <Button block size="large" onClick={signOut}>
            Log Out Session
          </Button>
        </Footer>
      )}
    </Page>
  );
}
